import React from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {Icon, OutlinedPillVariant, Pill, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import {AGENTIC_WORKERS_MESSAGE} from './agenticWorkersMessages';
import type {AgentDeclaration} from './agentDeclaration';
import {formatWaitingCount, useAgentStatusLineState} from './agentRunState';
import {formatRunTime, useMinuteTick, type RunTimeMessages} from './agentRunTime';

// Rendered whenever the agent is visible, even while the line is hidden, so the header can keep room for it.
export const STATUS_LINE_MARKER = 'data-agent-status-line';

const STATUS_LINE_DATA_CY = 'agent-status-line';
const SEPARATOR = '·';
const SYSTEM_TOKENS = tokensForThemeProvider.sky.sys;
const STATUS_LINE_RUN_TIME_MESSAGES: RunTimeMessages = {
	justNow: AGENTIC_WORKERS_MESSAGE.STATUS_LINE_RAN_JUST_NOW,
	at: AGENTIC_WORKERS_MESSAGE.STATUS_LINE_RAN_AT,
};

const STATUS_LINE_PART = {
	COUNT: 'COUNT',
	RAN: 'RAN',
	SCHEDULE_OFF: 'SCHEDULE_OFF',
} as const;
type StatusLinePartKey = (typeof STATUS_LINE_PART)[keyof typeof STATUS_LINE_PART];

type StatusLinePart = {
	key: StatusLinePartKey;
	content: React.ReactNode;
};

// The line's sparkle is one icon size smaller than the button's, so it sits half the difference further in to centre
// under it, and its text a further half on to start under the button's label.
const HALF_ICON_SIZE_DIFFERENCE = `(${SYSTEM_TOKENS.size.icon['2']} - ${SYSTEM_TOKENS.size.icon['1']}) / 2`;

// Hangs under its agent's control, so it never moves or widens the toolbar row. The inset is the button's padding and
// border.
const StatusLinePosition = styled.div`
	position: absolute;
	top: calc(100% + ${SYSTEM_TOKENS.spacing['100']});
	left: calc(${SYSTEM_TOKENS.spacing['300']} + ${SYSTEM_TOKENS.size.borderwidth.thin} + ${HALF_ICON_SIZE_DIFFERENCE});
	white-space: nowrap;
`;

// The gap is the button's gap between its icon and its label.
const StatusLineRow = styled.div`
	display: flex;
	align-items: center;
	gap: calc(${SYSTEM_TOKENS.spacing['200']} + ${HALF_ICON_SIZE_DIFFERENCE});
`;

const markerAttribute = {[STATUS_LINE_MARKER]: true};

const AuxiliaryText = ({children}: {children: React.ReactNode}): JSX.Element => (
	<Text size={'2'} color={'auxiliary'} noWrap={true}>
		{children}
	</Text>
);

const Separator = (): JSX.Element => (
	<div aria-hidden={true}>
		<AuxiliaryText>{SEPARATOR}</AuxiliaryText>
	</div>
);

// Plain text, not a live region: what it reflects is announced where it happens, and a live region would announce
// "Ran" again every minute as it ages.
export const AgentStatusLine = ({declaration}: {declaration: AgentDeclaration}): JSX.Element => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const {firstReadAnswered, isRunning, waitingCount, lastRunFinishedAt, scheduleOff} = useAgentStatusLineState(declaration);
	// Before the first read the counts are not known yet, and during a page run only a count still waiting is news.
	const isShowing = firstReadAnswered && (!isRunning || waitingCount > 0);
	const isShowingRan = isShowing && !isRunning && lastRunFinishedAt !== null;

	useMinuteTick(isShowingRan);

	const parts: StatusLinePart[] = [
		{
			key: STATUS_LINE_PART.COUNT,
			content:
				waitingCount > 0 ? (
					<Pill outlined={true} outlinedVariant={OutlinedPillVariant.ERROR}>
						{formatWaitingCount(intl, declaration, waitingCount)}
					</Pill>
				) : (
					<AuxiliaryText>
						{formatMessage(
							{id: AGENTIC_WORKERS_MESSAGE.STATUS_LINE_NONE_WAITING},
							{resultPlural: formatMessage(declaration.resultNoun.plural)}
						)}
					</AuxiliaryText>
				),
		},
	];
	if (isShowingRan) {
		parts.push({
			key: STATUS_LINE_PART.RAN,
			content: (
				<AuxiliaryText>
					{formatRunTime(lastRunFinishedAt, new Date(), intl, STATUS_LINE_RUN_TIME_MESSAGES)}
				</AuxiliaryText>
			),
		});
	}
	if (!isRunning && scheduleOff === true) {
		parts.push({
			key: STATUS_LINE_PART.SCHEDULE_OFF,
			content: <AuxiliaryText>{formatMessage({id: AGENTIC_WORKERS_MESSAGE.STATUS_LINE_SCHEDULE_OFF})}</AuxiliaryText>,
		});
	}

	return (
		<StatusLinePosition {...markerAttribute} data-cy={STATUS_LINE_DATA_CY}>
			{isShowing && (
				<StatusLineRow>
					<Icon
						icon={'sparkle'}
						size={'s'}
						customColor={SYSTEM_TOKENS.color.text.content.auxiliary}
						aria-hidden={true}
					/>
					{parts.map((part, index) => (
						<React.Fragment key={part.key}>
							{index > 0 && <Separator />}
							{part.content}
						</React.Fragment>
					))}
				</StatusLineRow>
			)}
		</StatusLinePosition>
	);
};
